import { useToast } from '@chakra-ui/react'
import { useDaily, useDailyEvent, useDevices } from '@daily-co/daily-react'
import React, { useEffect, useState } from 'react'
import { WaitingRoomPopover } from './components'
import { useTelehealthRecordingTimer } from './context/active-telehealth-session.context'
import { useClinicianDailyManager } from './context/clinician-daily-manager.context'
import { useClinicianTelehealthSession } from './context/clinician-telehealth-session.context'
import { usePlaySound } from './hooks/use-play-sound'

export const WaitroomPopoverManager = () => {
  const { activeSidebarTab, toggleSidebar } = useClinicianTelehealthSession()

  const { admitWaitingParticipant } = useClinicianDailyManager()

  const { playSound, sounds } = usePlaySound()
  const toast = useToast()
  const { hasFiveMinutesPassed } = useTelehealthRecordingTimer()

  const [waitingToasts, setWaitingToasts] = useState<Array<any>>([])
  const [activeToasts, setActiveToasts] = useState<Array<any>>([])

  const devices = useDevices()
  const playWelcomeSound = () => {
    playSound(
      sounds.NEW_PARTICIPANT,
      devices.currentSpeaker?.device.deviceId || 'default'
    )
  }

  useDailyEvent('waiting-participant-added', event => {
    setWaitingToasts(prev => [event.participant, ...prev])
    if (!hasFiveMinutesPassed) {
      playWelcomeSound()
    }
  })

  const daily = useDaily()

  useEffect(() => {
    if (activeSidebarTab === 'participants') {
      setWaitingToasts([])
      setActiveToasts([])
      toast.closeAll()
    }
  }, [activeSidebarTab, toast])

  useEffect(() => {
    const getWaitingRoomPopover = (
      participant: any,
      bottomPosition: string
    ) => (
      <WaitingRoomPopover
        hasFiveMinutesPassed={hasFiveMinutesPassed}
        onAdmit={() => {
          admitWaitingParticipant(participant.id)
          toast.close(participant.id)
        }}
        onClose={() => {
          toast.close(participant.id)
        }}
        participant={participant}
        openParticipantsPanel={() => {
          activeSidebarTab === 'participants'
            ? toast.closeAll()
            : toggleSidebar('participants')
        }}
        style={{ bottom: bottomPosition }}
      />
    )

    // Handle new participants that need toasts
    const newParticipants = waitingToasts.filter(
      p => !activeToasts.some(t => t.id === p.id)
    )

    // Create toasts for new participants
    if (newParticipants.length > 0) {
      const updatedActiveToasts = [...activeToasts]

      newParticipants.forEach(participant => {
        // Create a new toast with default bottom position
        toast({
          position: 'bottom-right',
          duration: 10000,
          id: participant.id,
          render: () => getWaitingRoomPopover(participant, '92px'),
          onCloseComplete: () => {
            setActiveToasts(prev => prev.filter(p => p.id !== participant.id))
            setWaitingToasts(prev => prev.filter(p => p.id !== participant.id))
          }
        })

        // Add to active toasts
        updatedActiveToasts.push(participant)
      })

      // Update active toasts state once
      setActiveToasts(updatedActiveToasts)
    }

    // Reposition all existing toasts
    const reversedToastIds = [...activeToasts.map(t => t.id)].reverse()
    reversedToastIds.forEach((id, index) => {
      const participant = waitingToasts.find(p => p.id === id)
      if (participant) {
        // Calculate position from bottom
        const position = `${92 + index * 154}px`

        // Update existing toast with new position
        toast.update(id, {
          position: 'bottom-right',
          duration: 10000,
          render: () => getWaitingRoomPopover(participant, position)
        })
      }
    })

    // Remove toasts for participants who are no longer in the waiting list
    activeToasts.forEach(active => {
      if (!waitingToasts.some(p => p.id === active.id)) {
        toast.close(active.id)
        setActiveToasts(prev => prev.filter(p => p.id !== active.id))
      }
    })
  }, [
    waitingToasts,
    activeToasts,
    toast,
    toggleSidebar,
    daily,
    hasFiveMinutesPassed,
    activeSidebarTab
  ])

  return null
}
